// Description: Page to add a new integration
// Updated: 09/10/2025
// Author: Simon Franklin

// React
import React from "react";

// Material UI Components
import { Box, Button, Typography, CircularProgress } from "@mui/material";

// Components and functions for the project
import HeaderPage from "../../shared/components/HeaderPage/HeaderPage";
import use123zapStore from "../../context/123zapStore";
import { getTokenData, notify } from "../../shared/utils/utils";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { consultIntegrations } from "../../services/api/Integrations/listIntegrations";
import {
  INTEGRATIONS_CONFIG,
  resolveIntegrationKey,
} from "../../shared/constants/integrations";

export const AlertIcon = () => {
  return (
    <svg
      width="18"
      height="17"
      viewBox="0 0 18 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="9.25" cy="8.5" r="8" stroke="#7C87AA" />
      <path
        d="M8.79364 12.9187V6.292H9.66164V12.9187H8.79364ZM9.23231 5.592C9.07675 5.592 8.94609 5.53911 8.84031 5.43333C8.73453 5.32756 8.68164 5.2 8.68164 5.05067C8.68164 4.90133 8.73453 4.77378 8.84031 4.668C8.94609 4.556 9.07675 4.5 9.23231 4.5C9.38164 4.5 9.5092 4.556 9.61497 4.668C9.72075 4.77378 9.77364 4.90133 9.77364 5.05067C9.77364 5.2 9.72075 5.32756 9.61497 5.43333C9.5092 5.53911 9.38164 5.592 9.23231 5.592Z"
        fill="#7C87AA"
      />
    </svg>
  );
};

const FORM_DATA_BY_INTEGRATION = {
  "Controllr ISP": ["url", "username", "password"],
  HubSoft: ["url", "client_id", "client_secret", "username", "password"],
  SGP: ["url", "app", "token"],
  EliteSoft: ["url", "_user", "_passwd"],
  Sienge: ["url", "username", "password"],
  "IXC Soft": ["url", "user_id", "token"],
  "Bemobi (7AZ)": ["sevaz_base_url", "sevaz_api_key"],
  Bemobi: ["sevaz_base_url", "sevaz_api_key"],
  "7AZ": ["sevaz_base_url", "sevaz_api_key"],
  "MK Solutions": ["url", "api_token", "api_password"],
  Atlaz: ["url", "token"],
  Atlas: ["url", "token"],
  Efi: [
    "url",
    "client_id",
    "client_secret",
    "certificate_pem",
    "certificate_key_pem",
  ],
  EFI: [
    "url",
    "client_id",
    "client_secret",
    "certificate_pem",
    "certificate_key_pem",
  ],
  "Efí": [
    "url",
    "client_id",
    "client_secret",
    "certificate_pem",
    "certificate_key_pem",
  ],
  Voalle: [
    "url",
    "username",
    "password",
    "client_id",
    "secret_id",
    "syndata",
  ],
  CobreFacil: ["app_id", "secret"],
  "Cobre Fácil": ["app_id", "secret"],
  Asaas: ["api_key"],
  "Receita NET": ["token"],
  ReceitaNet: ["token"],
  ISPFY: ["base_url", "token"],
  "LH ISP": ["parceiro_auth_token", "base_url"],
  "Superlógica Assinaturas": ["app_token", "access_token", "base_url"],
  Superlogica: ["app_token", "access_token", "base_url"],
  SuperlogicaAssinaturas: ["app_token", "access_token", "base_url"],
  ssOtica: ["access_token", "base_url"],
  "F5 ERP": ["base_url", "base_path", "api_key", "empresa_token"],
};

const FIELD_DISPLAY_LABELS = {
  sevaz_api_key: "Token",
  sevaz_base_url: "URL",
  parceiro_auth_token: "Token do parceiro",
  base_url: "URL base",
  app_token: "App token",
  access_token: "Access token",
  cnpj: "CNPJ",
  api_key: "X-Api-Key",
  empresa_token: "Token da empresa",
  base_path: "Caminho base",
};

const FIELD_DEFAULT_VALUES = {
  sevaz_base_url: "https://api.7az.com.br/v2/integrations/omnichannel",
};

const INTEGRATION_FIELD_DEFAULT_VALUES = {
  "LH ISP": {
    base_url: "https://sistema.celeretelecom.com",
  },
  "Superlógica Assinaturas": {
    base_url: "https://api.superlogica.net",
  },
  Superlogica: {
    base_url: "https://api.superlogica.net",
  },
  ssOtica: {
    base_url: "https://app.ssotica.com.br",
  },
  "F5 ERP": {
    base_url: "https://f5erp.com.br",
    base_path: "/originallis",
  },
};

const EFI_INTEGRATION_ID = "fa614ff0-0ec1-48d0-bb6c-b99f6cd0b94b";

const FORM_DATA_BY_INTEGRATION_ID = {
  [EFI_INTEGRATION_ID]: FORM_DATA_BY_INTEGRATION.Efi,
};

function getIntegrationFormFields(integration) {
  if (!integration) return [];

  const byId = FORM_DATA_BY_INTEGRATION_ID[integration.id];
  if (byId) return byId;

  const title = String(integration.title || "").trim();
  const key = String(integration.key || "").trim();
  const normalizedTitle = title.toLowerCase();
  const normalizedKey = key.toLowerCase();

  const directMatch =
    FORM_DATA_BY_INTEGRATION[title] ||
    FORM_DATA_BY_INTEGRATION[key] ||
    FORM_DATA_BY_INTEGRATION[title.toUpperCase()] ||
    FORM_DATA_BY_INTEGRATION[title.toLowerCase()];

  if (directMatch) return directMatch;

  const isEfiAlias =
    normalizedTitle.includes("efi") ||
    normalizedTitle.includes("efí") ||
    normalizedKey === "efi";

  if (isEfiAlias) return FORM_DATA_BY_INTEGRATION.Efi;

  return [];
}

function normalizeIntegrationName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "");
}

const AddIntegration = () => {
  const setIntegrations = use123zapStore((state) => state.setIntegrations);
  const integrations = use123zapStore((state) => state.integrations);

  useEffect(() => {
    if (!integrations || integrations.length === 0) {
      consultIntegrations()
        .then((response) => {
          setIntegrations(response.data);
        })
        .catch((error) => {
          console.error("Erro ao carregar integrações:", error);
        });
    }
  }, [integrations, setIntegrations]);

  const id = localStorage.getItem("integration_id") || null;
  const integrationTitle = localStorage.getItem("integration_title") || null;
  const normalizedStoredTitle = normalizeIntegrationName(integrationTitle);
  const setAlreadyFetchIntegrations = use123zapStore(
    (state) => state.setAlreadyFetchIntegrations,
  );
  const integrationsList = Array.isArray(integrations) ? integrations : [];
  const selectedIntegration =
    integrationsList.find((item) => item.id === id) ||
    integrationsList.find((item) => item.title === integrationTitle) ||
    integrationsList.find(
      (item) => normalizeIntegrationName(item.title) === normalizedStoredTitle,
    ) ||
    INTEGRATIONS_CONFIG.find((item) => item.id === id) ||
    INTEGRATIONS_CONFIG.find((item) => item.title === integrationTitle) ||
    INTEGRATIONS_CONFIG.find(
      (item) => normalizeIntegrationName(item.title) === normalizedStoredTitle,
    );
  const [disableButton, setDisableButton] = React.useState(false);
  const navigate = useNavigate();

  const initializeFormState = (fields, integration) => {
    const integrationDefaults =
      INTEGRATION_FIELD_DEFAULT_VALUES[integration?.title] || {};
    return fields.reduce((acc, field) => {
      acc[field] =
        integrationDefaults[field] ?? FIELD_DEFAULT_VALUES[field] ?? "";
      return acc;
    }, {});
  };

  const [formState, setFormState] = React.useState({});

  React.useEffect(() => {
    if (selectedIntegration) {
      const formFields = getIntegrationFormFields(selectedIntegration);
      setFormState(initializeFormState(formFields, selectedIntegration));
    }
  }, [selectedIntegration]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setDisableButton(true);

    const finalUrl = (integration) => {
      const integrationKey = resolveIntegrationKey(integration);
      if (integrationKey) return integrationKey;

      if (integration === "SGP") {
        return "sgp";
      } else if (integration === "Controllr ISP") {
        return "controllr";
      } else if (integration === "HubSoft") {
        return "hubsoft";
      } else if (integration === "EliteSoft") {
        return "elitesoft";
      } else if (integration === "Sienge") {
        return "sienge";
      } else if (integration === "IXC Soft") {
        return "ixc";
      } else if (
        integration === "Bemobi (7AZ)" ||
        integration === "Bemobi" ||
        integration === "7AZ"
      ) {
        return "bemobi";
      } else if (integration === "MK Solutions") {
        return "mksolutions";
      } else if (integration === "Atlaz" || integration === "Atlas") {
        return "atlaz";
      } else if (integration === "Efi" || integration === "EFI" || integration === "Efí") {
        return "efi";
      } else if (integration === "Voalle") {
        return "voalle";
      } else if (integration === "CobreFacil" || integration === "Cobre Fácil") {
        return "cobrefacil";
      } else if (integration === "Asaas") {
        return "asaas";
      } else if (integration === "Receita NET" || integration === "ReceitaNet") {
        return "receitanet";
      } else if (integration === "ISPFY") {
        return "ispfy";
      } else if (integration === "LH ISP") {
        return "lhisp";
      } else if (
        integration === "Superlógica Assinaturas" ||
        integration === "Superlogica" ||
        integration === "SuperlogicaAssinaturas"
      ) {
        return "superlogica";
      } else if (integration === "ssOtica") {
        return "ssotica";
      }
    };

    try {
      const { token, client_id } = await getTokenData();

      if (!token) throw new Error("Token de autenticação não encontrado");
      if (!client_id) throw new Error("ID do cliente não encontrado");

      const payload = { ...formState };

      await fetch(
        `${process.env.REACT_APP_BACKEND_AWS_URL}/erp/${finalUrl(
          selectedIntegration.title,
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${token}`,
            Client_ID: `${client_id}`,
          },
          body: JSON.stringify(payload),
        },
      )
        .then((response) => response.json())
        .then((response) => {
          if ((response.message || response.status) === "success") {
            notify("Success", "Integração adicionada com sucesso", true);
            setAlreadyFetchIntegrations(false);
            navigate("/integrações");
          }
        });
    } catch (error) {
      console.error(error);
      throw new Error("Erro ao buscar dados da API");
    } finally {
      setDisableButton(false);
    }
  };

  if (!selectedIntegration) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <HeaderPage />
      <Box
        component="main"
        sx={{
          display: "flex",
          padding: "32px",
          gap: { xs: "24px", sm: "64px" },
          justifyContent: { xs: "normal", sm: "space-between" },
          minHeight: "calc(100vh - 100px)",
          minWidth: "100%",
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box
          component="section"
          sx={{
            width: { xs: "100%", sm: "calc(100% - 32px)" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              height: "auto",
              borderRadius: "4px",
              border: "1px solid #E0E0E0",
              padding: "24px",
              backgroundColor: "#fff",
              gap: "24px",
            }}
          >
            <Box
              component="img"
              src={selectedIntegration.logo || selectedIntegration.icon}
              alt={`logo ${selectedIntegration.title}`}
              sx={{
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                backgroundColor: "#bdbdbd",
                minWidth: "32px",
                minHeight: "32px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "13px", sm: "16px" },
                }}
              >
                {selectedIntegration.title}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: "12px", sm: "16px" },
                }}
              >
                {selectedIntegration.description ||
                  selectedIntegration.text ||
                  selectedIntegration.desc}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          component="section"
          sx={{
            width: { xs: "100%", sm: "calc(100% - 32px)" },
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              border: "1px solid #d9d9dc",
              height: "100%",
              backgroundColor: "#fff",
              padding: { xs: "24px", sm: "48px" },
            }}
          >
            {Object.keys(formState).map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginBottom: "32px",
                }}
              >
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Typography
                    sx={{
                      textTransform: "uppercase",
                      fontSize: "17px",
                      fontWeight: 600,
                    }}
                  >
                    {FIELD_DISPLAY_LABELS[item] || item}
                  </Typography>
                  <AlertIcon />
                </Box>

                <input
                  onChange={(e) =>
                    setFormState((prev) => ({
                      ...prev,
                      [item]: e.target.value,
                    }))
                  }
                  type={
                    item === "password" ||
                    item === "sevaz_api_key" ||
                    item === "api_key" ||
                    item === "empresa_token"
                      ? "password"
                      : "text"
                  }
                  name={item}
                  placeholder={FIELD_DISPLAY_LABELS[item] || item}
                  value={formState[item]}
                  style={{
                    height: "48px",
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #E8E8E8",
                    backgroundColor: "#F8F9FB",
                    padding: "17px 24px",
                    outline: "none",
                  }}
                />
              </Box>
            ))}

            <Button
              variant="contained"
              disabled={disableButton}
              type="submit"
              sx={{
                height: "54px",
                width: "100%",
                borderRadius: "6px",
                fontSize: "18px",
                fontWeight: 500,
                marginTop: "2px",
              }}
            >
              Conectar
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddIntegration;
